import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box
} from '@mui/material';
import { useTheme } from '@mui/material';
import { getContrastText } from '../../utils/colorUtils';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';

const planFeatures = {
  basic: [
    // Limites de Cadastro
    { text: 'Cadastrar até 3 hábitos', available: true },
    { text: 'Cadastrar até 2 metas', available: true },
    { text: 'Cadastrar até 1 desafio', available: true },
    { text: 'Cadastrar até 3 notas', available: true },
    
    // Recursos de Produtividade
    { text: 'Até 2 vídeos do YouTube para foco', available: true },
    { text: '1 playlist do Spotify', available: true },
    
    // Recursos Indisponíveis
    { text: 'Vídeos ilimitados para foco', available: false },
    { text: 'Playlists ilimitadas do Spotify', available: false },
    { text: 'Acesso à aba Meditações', available: false },
    { text: 'Acesso à aba Estatísticas', available: false }
  ],
  pro: [
    // Recursos Ilimitados
    { text: 'Hábitos ilimitados', available: true },
    { text: 'Metas ilimitadas', available: true },
    { text: 'Desafios ilimitados', available: true },
    { text: 'Notas ilimitadas', available: true },
    
    // Recursos de Produtividade
    { text: 'Vídeos ilimitados do YouTube para foco', available: true },
    { text: 'Playlists ilimitadas do Spotify', available: true },
    
    // Recursos Adicionais
    { text: 'Acesso à aba Meditações', available: true },
    { text: 'Acesso à aba Estatísticas', available: true },
    { text: 'Categorização avançada', available: true }
  ]
};

export default function PlanComparisonDialog({ open, onClose, customColors }) {
  const theme = useTheme();
  const primaryColor = customColors?.primary || theme.palette.primary.main;
  const primaryTextColor = getContrastText(primaryColor);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        bgcolor: primaryColor,
        color: primaryTextColor,
        py: 2
      }}>
        Limite do Plano Básico Atingido
      </DialogTitle>
      <DialogContent sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Coluna do Plano Básico */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%', 
              position: 'relative',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                color: primaryColor,
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano Básico
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.basic.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {feature.available === false ? (
                      <CloseIcon color="error" />
                    ) : (
                      <CheckIcon sx={{ color: primaryColor }} />
                    )}
                    <Typography color="text.primary">
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Coluna do Plano PRO */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%',
              position: 'relative',
              bgcolor: primaryColor,
              color: primaryTextColor,
              '& .MuiTypography-root': {
                color: primaryTextColor
              }
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano PRO
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.pro.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckIcon sx={{ color: primaryTextColor }} />
                    <Typography>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        justifyContent: 'center',
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            mr: 2,
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: primaryColor,
              bgcolor: `${primaryColor}10`
            }
          }}
        >
          Continuar no Plano Básico
        </Button>
        <Button 
          variant="contained"
          sx={{ 
            bgcolor: primaryColor,
            color: primaryTextColor,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? `${primaryColor}dd`
                : `${primaryColor}bb`
            }
          }}
        >
          Fazer Upgrade para PRO
        </Button>
      </DialogActions>
    </Dialog>
  );
} 